import { MinimalUserDto, SaveAttachmentDto } from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  ControlledTextField,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useMonitorDeclineActionLoad,
  useMonitorDeclineActionSubmit,
} from '@issuemanager/apps/client/hooks';
import { useDeclineAction } from '@issuemanager/client/features/issues';
import { useUsers } from '@issuemanager/client/features/users';
import {
  DECLINE_REASON_LENGTH_MAX,
  DECLINE_REASON_LENGTH_MIN,
  IssuePermissionsEnum,
} from '@issuemanager/constants';
import { Stack, useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormProvider, RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueOutletContext } from '../../../hooks/useIssueOutletContext';
import { LayoutPanel } from '../layout';

type FormValues = {
  note: string;
  attachments: (SaveAttachmentDto & {
    addedBy: MinimalUserDto;
    createdAt: string;
  })[];
};

type LocalFields = 'note';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  actionId: string;
  actionTitle: string;
};

export function ActionDeclinePanel({
  onClose,
  isOpen,
  actionId,
  actionTitle,
}: Props) {
  const { t } = useTranslation('action');
  const { enqueueSnackbar } = useSnackbar();
  const { code: issueCode } = useIssueOutletContext();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();

  const monitorDeclineActionLoad = useMonitorDeclineActionLoad();
  const users = useUsers({ enabled: true, handleError: true });

  useAllQueries({
    queries: [users],
    onStart: () => {
      monitorDeclineActionLoad.start();
    },
    onSuccess: () => {
      monitorDeclineActionLoad.stop();
    },
    onError: () => {
      monitorDeclineActionLoad.error();
    },
  });

  const declineAction = useDeclineAction({
    code: issueCode,
    onSuccess: () =>
      enqueueSnackbar(t('manage.decline.success'), {
        variant: 'success',
      }),
  });

  const monitorDeclineActionSubmit = useMonitorDeclineActionSubmit();

  useAllMutations({
    mutations: [declineAction],
    onStart: () => {
      monitorDeclineActionSubmit.start();
    },
    onSuccess: () => {
      monitorDeclineActionSubmit.stop();
    },
    onError: () => {
      monitorDeclineActionSubmit.error();
    },
  });

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      note: '',
      attachments: [],
    },
  });

  const noteValue = form.watch('note');

  const rules: Record<LocalFields, RegisterOptions<FormValues>> = {
    note: {
      maxLength: {
        value: DECLINE_REASON_LENGTH_MAX,
        message: t('manage.decline.errors.note.maxLength'),
      },
      minLength: {
        value: DECLINE_REASON_LENGTH_MIN,
        message: t('manage.decline.errors.note.minLength'),
      },
    },
  };

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  const onSubmit = async (values: FormValues) => {
    await declineAction.mutateAsync({ id: actionId, ...values });
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={handleCancel}
      title={t('manage.decline.title')}
      description={t('manage.decline.description', {
        action: actionTitle,
      })}
      actionButton={{
        disabled: !noteValue.length || isUploadAttachmentsLoading,
        'data-testid': 'decline-footer-button',
      }}
      cancelButton={{
        disabled: isUploadAttachmentsLoading,
      }}
      actionButtonText={
        declineAction.isPending
          ? t('manage.decline.declining')
          : t('manage.decline.accept')
      }
      isLoading={declineAction.isPending}
      onSend={handleSubmit}
      content={
        <FormProvider {...form}>
          <Stack spacing={2}>
            <ControlledTextField
              name='note'
              label={t('manage.decline.label')}
              control={form.control}
              rules={rules.note}
              placeholder={t('manage.decline.placeholder')}
              multiline
              minRows={6}
              data-testid='decline-action-reason'
              maxLength={DECLINE_REASON_LENGTH_MAX}
            />
            <ControlledFileUploader
              control={form.control}
              name='attachments'
              disabled={declineAction.isPending}
              fullWidth
              permissions={[IssuePermissionsEnum.IsActionApprover]}
            />
          </Stack>
        </FormProvider>
      }
    />
  );
}
